import { Transaction, EditorState, Selection } from 'prosemirror-state'
import { Mapping, ReplaceStep, replaceStep } from 'prosemirror-transform'
import { addInsertionMarks } from './addInsertionMarks'
import { addDeletionMarks } from './addDeletionMarks'
import {
  deletingEmptyParagraphBeforeNonEmptyParagraph,
  formattingImage,
  insertingEmptyParagraphBeforeDeletedEmptyParagraph,
  isNodeDeletedParagraph,
  singleTagOpenDepthSlice,
} from './utils'
import { addWrappingMarks } from './addWrappingMarks'
import { schema } from 'prose-core'
import { Slice } from 'prosemirror-model'
import { Context } from './Types'

/**
 * Wrap a ReplaceStep and enrich the changed content with tracked changes marks
 * and attributes.
 *
 * @param step - the step to track
 * @param userId - the id of the user making the change
 * @param state - the state of the editor
 * @param originalTr - the original transaction
 * @param tr - the wrapped transaction
 * @param map - the mapping accumulated during the wrapping
 * @param index - the index of the step in the transaction being wrapped
 *
 * ---
 *
 * We hijack the step and insert the replacement at the end of the range.
 * This is to preserve all the content being deleted, and be able to track
 * the replaced content as a deletion.
 * For example, consider the step:
 *
 * Hello world
 *   ↓ ↓ ↓ ↓
 * Hello there
 *
 * We want this to:
 * - insert "there" right after "world", and mark it as an insertion
 * - mark the "world" as a deletion
 */
export function trackReplaceStep(
  step: ReplaceStep,
  userId: number,
  state: EditorState,
  originalTr: Transaction,
  tr: Transaction,
  map: Mapping,
  index: number,
  context?: Context,
) {
  if (formattingImage(step, state)) {
    const oldNode = state.doc.nodeAt(step.from)
    const newNode = step.slice.content.firstChild
    if (!oldNode || !newNode) {
      return
    }

    addWrappingMarks(tr, step.from, oldNode, newNode, userId, context)
    return
  }

  if (deletingEmptyParagraphBeforeNonEmptyParagraph(originalTr)) {
    const $pos = tr.selection.$from
    const before = tr.doc.resolve($pos.before()).nodeBefore

    if (before) {
      const step = new ReplaceStep($pos.pos - 2, $pos.pos, Slice.empty)
      if (step) {
        trackDeletion(step, userId, tr, map, context)
        return
      }
    }
  }

  if (insertingEmptyParagraphBeforeDeletedEmptyParagraph(originalTr)) {
    const curNode = tr.doc.nodeAt(tr.selection.$anchor.before())
    if (isNodeDeletedParagraph(curNode)) {
      tr.setNodeMarkup(
        tr.selection.$anchor.before(),
        undefined,
        Object.assign({}, curNode.attrs, { trackedChanges: null }),
      )
      return
    }
    const nextNode = tr.doc.nodeAt(tr.selection.$anchor.after())
    if (isNodeDeletedParagraph(nextNode)) {
      tr.setNodeMarkup(
        tr.selection.$anchor.after(),
        undefined,
        Object.assign({}, nextNode.attrs, { trackedChanges: null }),
      )
      return
    }
    const prevNode = tr.selection.$anchor.before() > 1 ? tr.doc.nodeAt(tr.selection.$anchor.before() - 2) : null
    if (isNodeDeletedParagraph(prevNode)) {
      tr.setNodeMarkup(
        tr.selection.$anchor.before() - 2,
        undefined,
        Object.assign({}, prevNode.attrs, { trackedChanges: null }),
      )
      return
    }
  }

  const { slice } = step

  if (slice.size > 0 && !singleTagOpenDepthSlice(slice)) {
    trackInsertion(step, userId, state, originalTr, tr, map, index, context)
  }

  if (step.from !== step.to) {
    trackDeletion(step, userId, tr, map, context)
  }
}

function trackInsertion(
  originalStep: ReplaceStep,
  userId: number,
  state: EditorState,
  originalTr: Transaction,
  tr: Transaction,
  map: Mapping,
  index: number,
  context?: Context,
) {
  const invertStep = originalStep.invert(originalTr.docs[index]).map(map)
  if (!invertStep) {
    throw new Error('Could not track change')
  }
  map.appendMap(invertStep.getMap())

  /*
   * We apply the current wrapped transaction to the state, and use the
   * resulting doc to slice out the inserted content.
   */
  const insertionTr = state.apply(tr).tr

  const step = replaceStep<typeof schema>(insertionTr.doc, originalStep.to, originalStep.to, originalStep.slice) as
    | ReplaceStep<typeof schema>
    | undefined
    | null
  if (!step) {
    throw new Error('Could not track change')
  }
  if (insertionTr.maybeStep(step).failed) {
    throw new Error('Could not track change')
  }

  /*
   * We calculate the end of the range resulting from inserting the
   * replacement at the end of the replaced range.
   */
  const mappedStepTo = step.getMap().map(step.to)
  addInsertionMarks(insertionTr, step.from, mappedStepTo, userId, context)
  const condensedStep = new ReplaceStep(step.from, step.to, insertionTr.doc.slice(step.from, mappedStepTo))
  tr.step(condensedStep)
  const mirrorIndex = map.maps.length - 1
  map.appendMap(condensedStep.getMap(), mirrorIndex)
  if (!tr.selection.eq(insertionTr.selection)) {
    tr.setSelection(Selection.fromJSON(tr.doc, insertionTr.selection.toJSON()))
  }
}

function trackDeletion(originalStep: ReplaceStep, userId: number, tr: Transaction, map: Mapping, context?: Context) {
  map.appendMapping(addDeletionMarks(tr, originalStep.from, originalStep.to, userId, context))
}
